import { useEffect, useRef, useState } from "react";
import "../../styles/research-query.scss";
import "../../styles/_mixedQueryV2.scss";
import {
  Button,
  Box,
  IconButton,
  Grid,
  Autocomplete,
  TextField,
  ListItemText,
  Tooltip,
} from "@mui/material";
import ResourceQueryTab from "../../components/ResearchQuery/ResourceQueryTab";
import ExternalQueryTab from "../../components/ResearchQuery/ExternalQueryTab";
import MergeQueryTab from "../../components/ResearchQuery/MergeQueryTab";
import { IResearchQuery } from "../../types/researchQuery";
import { useRuleResourceContext } from "../../contexts/RuleResourceContext";
import Loader from "../../components/Molecules/Loader/Loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useFetchRuleById from "../../hooks/useFetchRuleById";
import useFetchResearchQueryByRuleId from "../../hooks/useFetchResearchQueryByRuleId";
import { GridColDef } from "@mui/x-data-grid";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import { createSetLoading, useToast } from "../../services/utils";
import {
  addResearchQuery,
  getResearchQueryDetail,
  updateResearchQuery,
} from "../../services/rulesService";
import { cloneComparisonResearchQuery } from "../../services/comparisonResearchQueries";
import DataTable from "../../components/DataGrids/DataGrid";
import {
  ruleQueriesType,
  ruleSubQueriesType,
} from "../../services/constants";
import { replaceQueryFromDomainMapped } from "../../services/utils";
import ReadMoreLess from "../../components/ReadMoreLess";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import useFetchLinkedServices from "../../hooks/useFetchLinkedServices";
import useFetchResourcesByIdMultipleIds from "../../hooks/useFetchResourcesByMultipleIds";
import useFetchAllResources from "../../hooks/useFetchAllResources";
import useFetchResourceColumnsByMultipleIds from "../../hooks/useFetchResourceColumnsByMultipleIds";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import MixedQueryEditorDialog from "../ResearchQuery/MixedQueryV2/MixedQueryEditorDialog";
import useMixedQueryV2State from "../ResearchQuery/MixedQueryV2/useMixedQueryV2State";
import {
  MAX_SUBQUERIES,
  buildMixedSource,
  getSubQueryLabel,
  hydrateResourceColumns,
  hydrateResourceColumnsForResource,
  mapSourceToSubQueries,
  resolveMixedQueryReadOnly,
} from "../ResearchQuery/MixedQueryV2/mixedQueryV2.utils";
import {
  IconBtnEditBase,
  IconDeleteBlueSvg,
  IconEditBase,
} from "../../common/utils/icons";
import { useAuth } from "../../contexts/AuthContext";
import AutoCompleteRuleList from "../../components/Molecules/Rule/AutoCompleteRuleList";
import { comparisonResearchQuerySchema } from "../../schemas";
import CloneComparisonResearchQueryDialog from "../../components/Dialogs/CloneComparisonResearchQueryDialog";
import CommentBeforeUpdateDialog from "../../components/Dialogs/CommentBeforeUpdateDialog";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import {
  validateConfigRequest,
  getResourceColumnsByMultipleIds,
} from "../../services/resourcesService";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { useAuthTagContext } from "../../contexts/TagAndSystemProvider";
import useFetchActiveAuthTagsList from "../../hooks/useFetchActiveAuthTagsList";
import useOptimizedConnectionKeys from "../../utils/useOptimizedConnectionKeys";

interface RuleResearchQueryProps {
  researchQuery?: any;
  setResearchQuery?: any;
  isFromExecution?: boolean;
  isEditResearchQuery?: boolean;
  setIsEditResearchQuery?: any;
  ruleDetailData?: any;
  isViewOnly?: boolean;
  allowExecutionOverride?: boolean;
}

export default function RuleResearchQuery({
  researchQuery,
  setResearchQuery,
  isFromExecution = false,
  isEditResearchQuery = false,
  setIsEditResearchQuery,
  ruleDetailData,
  isViewOnly = false,
  allowExecutionOverride = false,
}: RuleResearchQueryProps = {}) {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const { hasPermission } = useAuth();
  const canAddRuleResearchQuery = hasPermission(
    "comparison_research_query",
    "add",
  );
  const canEditRuleResearchQuery = hasPermission(
    "comparison_research_query",
    "edit",
  );

  const {
    isLoading,
    setIsLoading,
    setResourceIds,
    allResourcesData,
    setAllResourcesData,
    resourceData,
    setResourceData,
    resourceColIds,
    setResourceColIds,
    resourceColumnsData,
    setResourceColumnsData,
    globalVariables,
    setGlobalVariables,
    linkedServicesData,
    setLinkedServicesData,
    fetchedConnectionKeys,
    setFetchedConnectionKeys,
    setQueryBuilderTempValue,
    editorRef,
    resourceIds,
    setShowAggregatedColumns,
    viewInlineVariables,
  } = useRuleResourceContext();
  const { id: currentRuleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state;
  const { showToast } = useToast();
  const [loadingStates, setLoadingStates] = useState({
    ruleById: false,
    researchQueryByRuleId: false,
    allResources: false,
    linkedService: false,
    connectionKey: false,
    resourceByMultipleId: false,
    resourceColumnsByMultipleId: false,
    cloneResearchQuery: false,
    authTags: false,
  });
  const isFromListPage = state?.isFromListPage || false;
  const setLoading = createSetLoading(setLoadingStates);
  const [isAddModeActive, setIsAddModeActive] = useState(
    isFromListPage || false,
  );
  const [columns, setColumns] = useState<any>({
    resourceColumns: [],
    missingColumns: [],
    mismatchedColumns: [],
    mergedColumns: [],
    mixedQueryData: [
      {
        name: "",
        columns: [],
      },
    ],
  });
  const [selectedRule, setSelectedRule] = useState<{
    id: number | null;
    code: string;
    rule_tags_details: any[];
  }>({
    id: null,
    code: "",
    rule_tags_details: [],
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [ruleDetails] = useFetchRuleById({
    setIsLoading: setLoading("ruleById"),
    currentRuleId: selectedRule?.id ?? currentRuleId,
    isFromExecution,
  });
  const [researchQueryDetail] = useFetchResearchQueryByRuleId({
    setIsLoading: setLoading("researchQueryByRuleId"),
    currentRuleId: selectedRule?.id ?? currentRuleId,
    isFromExecution,
  });
  const [linkedServices] = useFetchLinkedServices({
    setIsLoading: setLoading("linkedService"),
  });
  const [resourcesData] = useFetchResourcesByIdMultipleIds({
    resourceIds,
    setIsLoading: setLoading("resourceByMultipleId"),
  });
  const [allResources] = useFetchAllResources({
    setIsLoading: setLoading("allResources"),
  });
  const [resourceColumns] = useFetchResourceColumnsByMultipleIds({
    resourceColIds,
    setIsLoading: setLoading("resourceColumnsByMultipleId"),
  });

  // Get the setter function from context (following linkedServicesData pattern)
  const { setAllAuthTagsData } = useAuthTagContext();

  // Fetch auth tags data directly in this component (following linkedServicesData pattern)
  const [authTagsData] = useFetchActiveAuthTagsList({
    setIsLoading: setLoading("authTags"),
  });

  // Update context state when auth tags data is fetched (following linkedServicesData pattern)
  useEffect(() => {
    if (authTagsData) {
      setAllAuthTagsData(authTagsData);
    }
  }, [authTagsData, setAllAuthTagsData]);

  useEffect(() => {
    if (linkedServices) {
      setLinkedServicesData(linkedServices);
    }
  }, [linkedServices]);

  useEffect(() => {
    if (resourcesData) {
      setResourceData(resourcesData);
    }
  }, [resourcesData]);

  useEffect(() => {
    if (allResources) {
      setAllResourcesData(allResources);
    }
  }, [allResources]);

  useEffect(() => {
    if (resourceColumns) {
      setResourceColumnsData(resourceColumns);
    }
  }, [resourceColumns]);

  // Use props if provided, otherwise use component state
  const [localResearchQueryDetailData, setLocalResearchQueryDetailData] =
    useState<any>({});
  // DQ-8540 (re-open) Fix #1: When navigating to a different comparison research
  // query view/edit URL, the React Router component is reused instead of
  // remounted. This means `selectedRule` from the previous rule persists and
  // shadows the new `currentRuleId` URL param in both fetch hooks, causing the
  // hooks to keep fetching (and displaying/breadcrumbing) the OLD rule.
  // Reset `selectedRule` whenever the URL param changes so the hooks fall back
  // to `currentRuleId` until ruleDetails arrives and re-populates the state.
  useEffect(() => {
    setSelectedRule({ id: null, code: "", rule_tags_details: [] });
  }, [currentRuleId]);

  useEffect(() => {
    if (ruleDetails) {
      setSelectedRule({
        id: ruleDetails?.id,
        code: ruleDetails?.code,
        rule_tags_details: ruleDetails?.rule_tags_details,
      });
      setLocalRuleData(ruleDetails);
    }
  }, [ruleDetails]);
  const [localRuleData, setLocalRuleData] = useState<any>({});
  const [localIsEditQuery, setLocalIsEditQuery] = useState<any>(false);

  // Determine which data and setter to use based on props
  const researchQueryDetailData = researchQuery || localResearchQueryDetailData;
  const setResearchQueryDetailData =
    setResearchQuery || setLocalResearchQueryDetailData;
  const ruleData = ruleDetailData || localRuleData;

  const isEditQuery = isEditResearchQuery || localIsEditQuery;
  const setIsEditQuery = setIsEditResearchQuery || setLocalIsEditQuery;

  // Function to update the parent component's state
  const handleSetResearchQueryDetailData = (newData: any) => {
    if (setResearchQuery) {
      // If we have a setter from props, use it to update the parent
      setResearchQuery(newData);
    } else {
      // Otherwise, update our local state
      setResearchQueryDetailData(newData);
    }
  };

  const [queryType, setQueryType] = useState("");
  const [editFormData, setEditFormData] = useState();

  const [checked, setChecked] = useState(false);
  const [researchQueryData, setResearchQueryData] = useState<IResearchQuery[]>(
    [],
  );

  // Mixed Query V2 state (parent owns the query name and main/report query;
  // sub-query editing state lives in the useMixedQueryV2State hook below).
  const [mixedQueryName, setMixedQueryName] = useState<string>("");
  const [mixedMainQuery, setMixedMainQuery] = useState<string>("");
  const {
    subQueries: mixedSubQueries,
    setSubQueries: setMixedSubQueries,
    activeIndex: mixedActiveIndex,
    setActiveIndex: setMixedActiveIndex,
    handleTypeChange: mixedHandleTypeChange,
    handleFieldChange: mixedHandleFieldChange,
    handleLinkedServiceChange: mixedHandleLinkedServiceChange,
    handleQueryChange: mixedHandleQueryChange,
    handleEditorFocus: mixedHandleEditorFocus,
    handleEditorBlur: mixedHandleEditorBlur,
    addSubQuery: mixedAddSubQuery,
    removeSubQuery: mixedRemoveSubQuery,
    handleToggleActive: mixedHandleToggleActive,
    getDependencyDisplayLabel: mixedGetDependencyDisplayLabel,
    handleSetExternalColumns: mixedHandleSetExternalColumns,
    mixedErrors,
    setMixedErrors,
    validateMixedQuery,
  } = useMixedQueryV2State({
    columns,
    setColumns,
    resourcesData: allResourcesData,
    maxSubQueries: MAX_SUBQUERIES,
    onFetchResourceColumns: (resourceId: number) => {
      hydrateResourceColumnsForResource(
        resourceId,
        allResourcesData,
        setColumns,
        getResourceColumnsByMultipleIds,
      ).catch(() => null);
    },
  });

  // Clone comparison research query state
  const [
    cloneComparisonResearchQueryDialog,
    setCloneComparisonResearchQueryDialog,
  ] = useState<boolean>(false);
  const [
    cloneComparisonResearchQueryData,
    setCloneComparisonResearchQueryData,
  ] = useState<any>({
    name: "",
    code: "",
    comparison_research_query_tags: [],
  });

  // Comment before update dialog state
  const [openCommentConfirmation, setOpenCommentConfirmation] =
    useState<boolean>(false);
  const [comment, setComment] = useState<string>("");
  const [pendingResearchQueryPayload, setPendingResearchQueryPayload] =
    useState<any>(null);
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);

  // OPTIMIZATION: Use custom hook for optimized connection key fetching
  // This replaces the inefficient useFetchConnectionKeysAll call with targeted fetching
  // CRITICAL FIX: Include both researchQueryDetailData (from API) and researchQueryData (local state)
  // to ensure connection keys are fetched even when local state is updated during edit operations

  const connectionKeys = useOptimizedConnectionKeys({
    dataSources: [
      researchQueryDetailData,
      { research_queries: researchQueryData }, // Wrap array in object to match expected structure
    ],
    setIsLoading: setLoading("connectionKey"),
    setFetchedConnectionKeys,
  });

  useEffect(() => {
    if (queryType === "Merged_Data") {
      if (columns) {
        setColumns((prev: any) => ({
          ...prev,
          resourceColumns: [],
          mergedColumns: columns?.missingColumns,
        }));
      }
    } else if (queryType === "Missing_Data") {
      if (columns) {
        setColumns((prev: any) => ({
          ...prev,
          resourceColumns: [],
          mergedColumns: columns?.missingColumns,
        }));
      }
    } else if (queryType === "Mismatched_Data") {
      if (columns) {
        setColumns((prev: any) => ({
          ...prev,
          resourceColumns: [],
          mergedColumns: columns?.mismatchedColumns,
        }));
      }
    }
  }, [queryType]);

  const handleTransition = (isChecked: boolean, actionType?: string) => {
    setIsAddModeActive(isChecked);
    if (actionType === "add") {
      setColumns((prev: any) => ({
        ...prev,
        mergedColumns: [],
        resourceColumns: [],
      }));
    }
  };

  useEffect(() => {
    if (!researchQueryDetail) return;

    // Case 1: Not from execution → set main detail + breadcrumb
    if (!isFromExecution) {
      setResearchQueryDetailData(researchQueryDetail);
      setCurrentBreadcrumbPage({
        name: researchQueryDetail?.code,
        id: researchQueryDetail?.id,
      });
    }

    // Case 2: researchQuery not present → update local state
    if (!researchQuery) {
      setLocalResearchQueryDetailData(researchQueryDetail);
    }

    // Cleanup for breadcrumb
    return () => {
      if (!isFromExecution) {
        setCurrentBreadcrumbPage({ name: "", id: null });
      }
    };
  }, [researchQueryDetail, researchQuery, isFromExecution]);

  useEffect(() => {
    if (researchQueryDetailData) {
      const updatedResearchQueryData =
        researchQueryDetailData?.research_queries?.map(
          (researchQuery: any, index: number) => {
            return {
              ...researchQuery,
              source: Array.isArray(researchQuery?.source)
                ? researchQuery.source[0]?.type === "Mixed"
                  ? researchQuery.source
                  : researchQuery.source[0]
                : researchQuery?.source,

              id: index + 1,
              updatedQuery: researchQuery?.query,
              query: replaceQueryFromDomainMapped(
                researchQuery?.query,
                columns?.missingColumns ?? [],
              ),
            };
          },
        );
      setResearchQueryData(updatedResearchQueryData ?? []);
      if (!isFromExecution)
        setGlobalVariables(researchQueryDetailData?.variables);
    }
  }, [researchQueryDetailData, columns?.missingColumns]);
  useEffect(() => {
    if (ruleData?.rule_schema?.resources?.length > 0) {
      setResourceIds(ruleData.rule_schema.resources);
    }
  }, [ruleData]);

  useEffect(() => {
    let resColIds: any[] = [];
    if (resourceData && resourceData.length > 0) {
      resourceData.map((res: any) => {
        resColIds.push(res.additional_properties?.resource_column_details_id);
      });
      setResourceColIds(resColIds);
    }
  }, [resourceData]);
  useEffect(() => {
    let missingColumns: any[] = [];
    let uniqueColumns: any[] = [];
    let baseColumns: any[] =
      ruleData?.rule_schema?.merge_rule?.primary_dataset?.merge_on.map(
        (column: any) => ({ name: `Key_${column}`, value: `Key_${column}` }),
      );
    if (resourceColumnsData && Array.isArray(resourceColumnsData)) {
      resourceColumnsData.forEach((resCol: any) => {
        const matchedRes: any = resourceData.find(
          (res: any) =>
            res?.additional_properties?.resource_column_details_id ===
            resCol?.id,
        );
        if (
          ruleData?.rule_schema?.merge_rule?.primary_dataset?.resource_id ===
          matchedRes?.id
        ) {
          uniqueColumns =
            ruleData?.rule_schema?.merge_rule?.primary_dataset?.merge_on;
        } else {
          const matchingResource =
            ruleData?.rule_schema?.merge_rule?.secondary_datasets.find(
              (rule: any) => rule.resource_id === matchedRes?.id,
            );
          if (matchingResource) {
            uniqueColumns = matchingResource.merge_on;
          }
        }
        const missing = getMappedColumns(
          uniqueColumns,
          resCol?.resource_column_properties?.resource_columns,
          matchedRes ? matchedRes?.code : "",
        );

        missingColumns = [...missingColumns, ...missing];
      });
      if (
        baseColumns &&
        baseColumns.length > 0 &&
        missingColumns &&
        missingColumns.length > 0
      ) {
        const updatedBaseColumns = updateBaseColumns(
          baseColumns,
          missingColumns,
        );
        // missingColumns = [...updatedBaseColumns, ...missingColumns];
        missingColumns = updatedBaseColumns;
      }
    } else {
      missingColumns = getMappedColumns(
        resourceColumnsData?.resource_column_properties?.unique_columns,
        resourceColumnsData?.resource_column_properties?.resource_columns,
        "",
      );
    }
    setColumns((prev: any) => ({
      ...prev,
      missingColumns: missingColumns,
      mismatchedColumns: missingColumns,
    }));
  }, [resourceColumnsData, resourceData, ruleData]);

  const updateBaseColumns = (baseColumn: any[], missingColumns: any[]) => {
    // Function to extract the resource from the column name
    const getResource = (columnName: string) => {
      return columnName.split(".")[0];
    };

    // Function to normalize and extract the relevant part of the value
    const extractValue = (value: string) => {
      if (value === undefined) return;
      const parts = value.split(".");
      return parts.length > 1 ? parts[1] : parts[0];
    };

    // Group missingColumns by resource
    const resources = missingColumns.reduce((acc, item) => {
      const resource = getResource(item.name);
      if (!acc[resource]) acc[resource] = [];
      acc[resource].push(item);
      return acc;
    }, {});

    // Ensure each resource has the same number of objects
    const resourceKeys = Object.keys(resources);
    const numColumns = resources[resourceKeys[0]].length;

    const updatedBaseColumn = [...baseColumn];

    // Iterate over each index
    for (let i = 0; i < numColumns; i++) {
      const valuesAtIndex = resourceKeys.map((resource) => {
        return extractValue(resources[resource][i]?.value); // Extract the relevant part of the value
      });

      // Check if all values at this index match and are not null
      const allMatch = valuesAtIndex.every(
        (value) => value !== null && value === valuesAtIndex[0],
      );

      // Update baseColumn if all values match and none of them are null
      if (allMatch && valuesAtIndex[0] !== "null") {
        updatedBaseColumn[i].value = `Key_${valuesAtIndex[0]}`;
        updatedBaseColumn[i].name = `Key_${valuesAtIndex[0]}`;
      }
    }

    return updatedBaseColumn;
  };

  const getMappedColumns = (
    columns: string[],
    allColumns: any[],
    prefix: string,
  ): { name: string; value: string }[] => {
    const result: { name: string; value: string }[] = [];
    if (columns) {
      for (const column of columns) {
        const matchedColumn =
          allColumns && allColumns.find((col) => col.column_name === column);

        if (matchedColumn) {
          result.push({
            name: `${prefix}.${matchedColumn.column_name}`,
            value:
              `${prefix}.${matchedColumn.domain_column}` ||
              `${prefix}.${matchedColumn.column_name}`,
          });
        }
      }
    }
    return result;
  };

  const validateField = async (name: any, value: any) => {
    try {
      // Create a partial form data object with only the field being changed
      const partialFormData = { rule_id: selectedRule?.id, [name]: value };
      await comparisonResearchQuerySchema.validateAt(name, partialFormData);
      // If validation passes, clear any previous errors for this field
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        [name]: undefined,
      }));
    } catch (validationError: any) {
      // If validation fails, set the error message for this field
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };

  // Hydrate the Mixed V2 editor from editFormData once when a Mixed row opens.
  // Guarded so it does not re-run when columns/resources load asynchronously.
  const mixedHydratedIdRef = useRef<number | null>(null);
  useEffect(() => {
    const isMixedEdit =
      queryType === "Mixed" && isEditQuery && !!(editFormData as any);
    if (!isMixedEdit) {
      mixedHydratedIdRef.current = null;
      return;
    }
    const target: any = editFormData;
    if (mixedHydratedIdRef.current === target?.id) return;
    mixedHydratedIdRef.current = target?.id ?? null;
    const source = Array.isArray(target?.source)
      ? target.source
      : target?.source
        ? [target.source]
        : [];
    setMixedQueryName(target?.name || "");
    setMixedMainQuery(target?.query || "");
    setMixedSubQueries(mapSourceToSubQueries(source, linkedServicesData));
    setMixedActiveIndex(0);
    hydrateResourceColumns(
      source,
      allResourcesData,
      setColumns,
      getResourceColumnsByMultipleIds,
    ).catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryType, isEditQuery, editFormData, linkedServicesData, allResourcesData]);

  // Entry point for the Mixed Query V2 module (mirrors the legacy "Configure"
  // button). Opening the editor is gated on isEditQuery, so a brand-new Mixed
  // query needs a blank editFormData seeded with the next id before the dialog
  // can render and save (handleSaveMixedQuery requires editFormData.id).
  const handleConfigureMixedQuery = () => {
    if (!editFormData) {
      const existing = researchQueryData ?? [];
      setEditFormData({
        id: existing.length > 0 ? existing.length + 1 : 1,
        name: "",
        query: "",
        source: [],
      } as any);
    }
    setIsEditQuery(true);
  };

  // Close the Mixed V2 editor and reset the Mixed edit state. Clears the
  // validation errors so a previously-edited query's errors don't leak into the
  // next query opened for editing.
  const handleCloseMixedDialog = () => {
    setIsEditQuery(false);
    setQueryType("");
    setEditFormData(undefined);
    setIsAddModeActive(true);
    setMixedErrors({});
    mixedHydratedIdRef.current = null;
  };

  // Persist the Mixed query: rebuild the edited entry's source/name/query and
  // hand the full research_queries array to the shared save flow (which runs
  // the comment-confirmation dialog in update mode).
  const handleSaveMixedQuery = () => {
    const current: any = editFormData;
    if (!current) return;
    // Field-level validation with parity to the legacy builder. Populates the
    // indexed mixedErrors object consumed by the V2 cards / Main Query editor.
    const isValid = validateMixedQuery(mixedMainQuery, mixedQueryName);
    if (!isValid) {
      showToast("Please fix validation errors", "error");
      return;
    }
    const existing = researchQueryData ?? [];
    const updatedEntry = {
      id: current.id,
      name: mixedQueryName,
      query: mixedMainQuery,
      source: buildMixedSource(mixedSubQueries),
    };
    const idx = existing.findIndex((q: any) => q.id === current.id);
    const updatedQueries =
      idx !== -1
        ? existing.map((q: any, i: number) => (i === idx ? updatedEntry : q))
        : [...existing, updatedEntry];
    setResearchQueryData(updatedQueries as IResearchQuery[]);
    handleSaveResearchQuery(updatedQueries as IResearchQuery[]);
    handleCloseMixedDialog();
  };

  // Update the Mixed query name and clear its validation error live once a
  // non-empty value is typed (parity with the sub-query field-level clearing).
  const handleMixedQueryNameChange = (value: string) => {
    setMixedQueryName(value);
    if (value?.trim()) {
      setMixedErrors((prev) => {
        if (!prev.name) return prev;
        const { name, ...rest } = prev;
        return rest;
      });
    }
  };

  // Update the Mixed Main Query and clear its validation error (index -1) live
  // once non-empty content is entered.
  const handleMixedMainQueryChange = (value: string) => {
    setMixedMainQuery(value);
    if (value?.trim()) {
      setMixedErrors((prev) => {
        if (!prev[-1]) return prev;
        const next = { ...prev };
        delete next[-1];
        return next;
      });
    }
  };

  const handleSaveResearchQuery = (researchQueryPayload: any) => {
    const updatedResearchQueryData = researchQueryPayload.map(
      (researchQuery: { source: any; updatedQuery: any; query: any }) => ({
        ...researchQuery,
        source: Array.isArray(researchQuery?.source)
          ? researchQuery.source
          : researchQuery?.source
            ? [researchQuery.source]
            : [],
        query: researchQuery?.updatedQuery ?? researchQuery.query,
      }),
    );
    // If in execution mode, just update parent state
    if (isFromExecution) {
      // Create a new object with the updated research queries
      const updatedResearchQuery = {
        ...researchQueryDetailData,
        research_queries: updatedResearchQueryData,
      };

      // Use the handler to update the parent's state
      handleSetResearchQueryDetailData(updatedResearchQuery);
      return;
    }

    // Handle optional rule - use selectedRule if available, otherwise use ruleData from URL params
    // If neither is available, allow null values (rule is optional)
    const currentRuleData = selectedRule?.id ? selectedRule : ruleData;
    const payload = {
      code: currentRuleData?.id ? `${currentRuleData.id}_RQ` : null,
      rule_id: currentRuleData?.id ?? null,
      rule_code: currentRuleData?.code ?? null,
      research_queries: updatedResearchQueryData,
      variables: globalVariables,
      is_active: true,
      comparison_research_query_tags:
        currentRuleData?.rule_tags_details?.map(
          (tag: any) => tag.auth_tag_id,
        ) ?? [],
    };
    const { comparison_research_query_tags, ...restPayload } = payload;
    const finalPayload = {
      comparison_research_query: { ...restPayload },
      comparison_research_query_tags,
    };
    if (
      researchQueryDetailData &&
      Object.keys(researchQueryDetailData).length > 0
    ) {
      // Update mode: show comment dialog before saving
      setPendingResearchQueryPayload(finalPayload);
      setOpenCommentConfirmation(true);
    } else {
      addResearchQuery(finalPayload)
        .then(async (response: any) => {
          if (response) {
            const ruleId: any = selectedRule?.id ?? currentRuleId;
            // Only fetch research query detail if we have a valid rule ID
            if (ruleId) {
              const result = await getResearchQueryDetail(ruleId);
              setResearchQueryDetailData(result);
            }
            showToast("Research Query created successfully!", "success");
          }
        })
        .catch((error: any) => {
          showToast(`Cannot create Research Query`, "error");
        });
    }
  };

  const handleCommentChange = (e: any) => {
    setComment(e.target.value);
  };

  const handleSaveComment = () => {
    setOpenCommentConfirmation(false);
    if (!pendingResearchQueryPayload) return;
    const payloadWithComment = {
      ...pendingResearchQueryPayload,
      comparison_research_query: {
        ...pendingResearchQueryPayload.comparison_research_query,
        comment,
      },
    };
    updateResearchQuery({
      currentResearchQueryId: researchQueryDetailData.id,
      payload: payloadWithComment,
    })
      .then((response: any) => {
        if (response)
          showToast("Research Query updated successfully!", "success");
      })
      .catch((err: Error) => {
        console.error(err);
        showToast(`Cannot update Research Query`, "error");
      });
    setComment("");
    setPendingResearchQueryPayload(null);
  };

  const handleCancelComment = () => {
    setOpenCommentConfirmation(false);
    setComment("");
    setPendingResearchQueryPayload(null);
  };

  const buildPayload = () => {
    const currentRuleData = selectedRule?.id ? selectedRule : ruleData;
    const updatedResearchQueryData = (researchQueryData ?? []).map(
      (rq: any) => ({
        ...rq,
        source: Array.isArray(rq?.source)
          ? rq.source
          : rq?.source
            ? [rq.source]
            : [],
        query: rq?.updatedQuery ?? rq.query,
      }),
    );
    const payload = {
      code: currentRuleData?.id ? `${currentRuleData.id}_RQ` : null,
      rule_id: currentRuleData?.id ?? null,
      rule_code: currentRuleData?.code ?? null,
      research_queries: updatedResearchQueryData,
      variables: globalVariables,
      is_active: true,
      comparison_research_query_tags:
        currentRuleData?.rule_tags_details?.map(
          (tag: any) => tag.auth_tag_id,
        ) ?? [],
    };
    const { comparison_research_query_tags, ...restPayload } = payload;
    return {
      comparison_research_query: { ...restPayload },
      comparison_research_query_tags,
    };
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const isEditMode = Boolean(researchQueryDetailData?.id);
      const validateReqBody = {
        target_endpoint: isEditMode
          ? `/comparison-research-queries/${researchQueryDetailData?.id}`
          : "/comparison-research-queries",
        method: isEditMode ? "PUT" : "POST",
        body: payload,
        query_params: {},
        url_params: isEditMode ? { id: researchQueryDetailData?.id } : {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) {
        showToast("Payload is valid!", "success");
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyPayload = async () => {
    const payload = buildPayload();
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    showToast("Payload copied to clipboard!", "success");
  };

  const getLinkedServiceCodebyId = (id: number | string | null | undefined) => {
    const linkedData = linkedServicesData?.find((item: any) => item.id === id);
    return linkedData ? linkedData.name : null;
  };
  const getConnectionKeybyId = (id: number | string | null | undefined) => {
    const connectionData = fetchedConnectionKeys?.find(
      (item: any) => item.id === id,
    );

    return connectionData ? connectionData?.name : null;
  };

  const researchColumns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      renderCell: (params: any) => <></>,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
    },
    {
      field: "id",
      headerName: "Id",
      minWidth: 50,
      // flex: 1,
      renderCell: (params: any) => <span>{params.value}</span>,
    },

    {
      field: "source",
      headerName: "Query Type",
      minWidth: 140,
      renderCell: (params: any) => {
        const value = params?.value;

        const type = Array.isArray(value) ? value?.[0]?.type : value?.type;

        return (
          <LimitChractersWithTooltip
            value={type ? type.replace("_", " ") : ""}
          />
        );
      },
    },
    {
      field: "name",
      headerName: "Query Name",
      minWidth: 250,
      flex: 1,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    {
      field: "query",
      headerName: "Query",
      minWidth: 250,
      // flex: 1,
      groupable: false,
      renderCell: (params: any) => {
        return (
          <>
            {params.value && params.value.length > 50 ? (
              <ReadMoreLess
                data={params?.value?.replace(/"/g, "")}
                charLimit={50}
              />
            ) : (
              params?.value?.replace(/"/g, "")
            )}
          </>
        );
      },
    },
    {
      field: "linked_service_id",
      headerName: "Connection Type",
      minWidth: 150,
      renderCell: (params: any) => {
        const linkedServiceCodebyId =
          getLinkedServiceCodebyId(params?.row?.source?.linked_service_id) ??
          "";

        return <LimitChractersWithTooltip value={linkedServiceCodebyId} />;
      },
    },
    {
      field: "connection_key_id",
      headerName: "Connection Key",
      minWidth: 150,
      renderCell: (params: any) => {
        const connectionKeybyId =
          getConnectionKeybyId(params?.row?.source?.connection_key_id) ?? "";

        return <LimitChractersWithTooltip value={connectionKeybyId} />;
      },
    },

    {
      field: "action",
      headerName: "Actions",
      width: 130,
      align: "center",
      headerAlign: "center",
      groupable: false,
      renderCell: (params: any) => {
        if (params.rowNode.type === "group") {
          return null;
        }
        const handleEditQuery = () => {
          const formData: any = researchQueryData.find(
            (query) => query.id === params.row.id,
          );
          const linkedService: any =
            linkedServicesData &&
            linkedServicesData?.find(
              (linked: any) =>
                linked?.id === formData?.source?.linked_service_id,
            );
          const connectionKey: any =
            fetchedConnectionKeys &&
            fetchedConnectionKeys?.find(
              (key: any) => key?.id === formData?.source?.connection_key_id,
            );
          const updatedFormData: any = {
            ...formData,
            source:
              formData?.source[0]?.type === "Mixed"
                ? formData.source.map((item: any) => ({
                    ...item,
                    sub_query_name: item.dependency_marker,
                  }))
                : formData.source,
            linked_service: linkedService,
            connection_key: connectionKey,
          };
          setEditFormData(updatedFormData);
          setQueryBuilderTempValue(formData?.query);
          setIsEditQuery(true);
          handleTransition(false);
          setQueryType(
            Array.isArray(params?.row?.source)
              ? params?.row?.source[0]?.type
              : params?.row?.source?.type,
          );
          if (isFromExecution) {
            setGlobalVariables(
              viewInlineVariables?.comparison_research_query_variables,
            );
          }
        };
        const handleDelete = async () => {
          const updatedQueryData = researchQueryData.filter(
            (query) => query.id !== params?.row?.id,
          );
          const normalizedQueryData = updatedQueryData.map((query) => ({
            ...query,
            source: Array.isArray(query.source) ? query.source : [query.source],
          }));
          const currentRuleData = selectedRule?.id ? selectedRule : ruleData;
          const payload = {
            code: `${currentRuleData?.id}_RQ`,
            rule_id: currentRuleData?.id,
            rule_code: currentRuleData?.code,
            research_queries: normalizedQueryData,
            variables: globalVariables,
          };
          const finalPayload = {
            comparison_research_query: payload,
            comparison_research_query_tags:
              researchQueryDetail?.comparison_research_query_tags_details?.map(
                (tag: any) => tag.auth_tag_id,
              ) ?? [],
          };
          updateResearchQuery({
            currentResearchQueryId: researchQueryDetailData.id,
            payload: finalPayload,
          })
            .then((response: any) => {
              if (response) {
                setResearchQueryData(updatedQueryData);
                showToast(
                  `${
                    params?.row?.name ?? "Research Query"
                  } deleted successfully!`,
                  "success",
                );
              }
            })
            .catch((err: Error) => {
              console.error("Error deleting research query:", err);
              showToast(`Cannot delete Research Query`, "error");
            });
          setEditFormData(undefined);
        };

        return (
          <>
            {!isViewOnly && canAddRuleResearchQuery && (
              <IconButton onClick={handleEditQuery}>
                <IconEditBase />
              </IconButton>
            )}
            {!isViewOnly && !isFromExecution && canAddRuleResearchQuery && (
              <IconButton onClick={handleDelete}>
                <IconDeleteBlueSvg />
              </IconButton>
            )}
          </>
        );
      },
    },
  ];
  const handleRuleChange = (event: any, value: any) => {
    if (value) {
      setSelectedRule({
        id: value.id,
        code: value.code,
        rule_tags_details: value?.rule_tags_details,
      });
    } else {
      // When rule is deselected, only clear rule-specific data
      // DO NOT clear queryType or other form field values - rule is now optional
      setSelectedRule({
        id: null,
        code: "",
        rule_tags_details: [],
      });
      // Clear only the research query data related to the previously selected rule
      setResearchQueryData([]);
      setResearchQueryDetailData({});
      // Clear global variables related to the rule
      setGlobalVariables(undefined);
      // Note: We intentionally do NOT reset queryType, isEditQuery, editFormData, or columns
      // This allows users to continue filling out the form even without a rule selected
    }
    validateField("rule_id", value?.id || null);
  };

  const handleSelectQueryType = (value: any) => {
    if (value) {
      setQueryType(value);
      if (value === "Resource" || value === "External" || value === "Mixed") {
        setQueryBuilderTempValue(`SELECT * FROM <>`);
      } else if (value === "Merged_Data") {
        setQueryBuilderTempValue("SELECT * FROM <MERGED_DATA>");
      } else if (value === "Missing_Data") {
        setQueryBuilderTempValue("SELECT * FROM <MISSING>");
      } else if (value === "Mismatched_Data") {
        setQueryBuilderTempValue("SELECT * FROM <MISMATCHED>");
      }
    } else {
      setQueryType("");
    }
  };

  const handleCancelClick = () => {
    if (isFromListPage) {
      // When cancelling edit or add operation, preserve the grid data
      // Only reset form-related state, NOT the grid data (researchQueryData/researchQueryDetailData)
      setQueryType("");
      setIsEditQuery(false);
      setIsAddModeActive(true);
      setEditFormData(undefined);
      setColumns({
        resourceColumns: [],
        missingColumns: [],
        mismatchedColumns: [],
        mergedColumns: [],
        mixedQueryData: [
          {
            name: "",
            columns: [],
          },
        ],
      });
      setShowAggregatedColumns(undefined);
      // DO NOT clear researchQueryData or researchQueryDetailData - this preserves grid data
      // DO NOT clear selectedRule - user's rule selection should remain
      // DO NOT clear context states - they're needed for the grid to display properly
    } else {
      // For Route 1: Maintain existing behavior (hide section)
      setQueryType("");
      handleTransition(false);
      setIsAddModeActive(false);
      setIsEditQuery(false);
    }
  };

  // Handle clone comparison research query
  const handleCloneComparisonResearchQuery = () => {
    if (!researchQueryDetailData?.id) {
      showToast("No research query to clone", "error");
      return;
    }
    setCloneComparisonResearchQueryData({
      comparison_research_query_id: researchQueryDetailData.id,
      rule_id: null,
    });
    setCloneComparisonResearchQueryDialog(true);
  };

  const handleCloseCloneComparisonResearchQuery = () => {
    setCloneComparisonResearchQueryDialog(false);
    setCloneComparisonResearchQueryData({
      rule_id: null,
    });
  };

  const onSaveCloneComparisonResearchQuery = () => {
    const comparisonResearchQueryId = Number(
      cloneComparisonResearchQueryData.comparison_research_query_id,
    );
    const ruleId = Number(cloneComparisonResearchQueryData.rule_id);

    cloneComparisonResearchQuery(comparisonResearchQueryId, ruleId)
      .then((response) => {
        showToast("Comparison Research Query cloned successfully!", "success");
        handleCloseCloneComparisonResearchQuery();
        // DQ-8540 Bug #2 & #3 fix:
        // Navigate to the cloned research query's view page so that:
        //   - the breadcrumb updates to reflect the cloned query's code, and
        //   - the manage-page's "overall rules list" (Configuration section) is
        //     no longer shown unexpectedly after the clone completes.
        // The API returns the newly-created comparison research query object;
        // use its rule_id to build the view URL (falling back to the selected
        // ruleId when the response is unavailable).
        const clonedRuleId = response?.rule_id ?? ruleId;
        navigate(`/comparison-research-queries/view/${clonedRuleId}`);
      })
      .catch(() => {
        showToast(`Cannot clone Comparison Research Query`, "error");
      });
  };

  return (
    <>
      {isFromListPage || isFromExecution
        ? null
        : Object.values(loadingStates).some(Boolean) && <Loader isLoading />}
      {/* <Box className="dashboard-title-group mb-0">
        <Grid container rowSpacing={2.5} columnSpacing={4}>
          <Grid item xs sx={{ justifyContent: "flex-end", display: "flex" }}>
            <Button
              variant="contained"
              color="secondary"
              className="btn-orange"
              onClick={handleSaveResearchQuery}
            >
              <SaveOutlinedIcon /> &nbsp; Save Research Query
            </Button>
          </Grid>
        </Grid>
      </Box> */}
      {(isAddModeActive || isEditQuery) && !isViewOnly && (
        <Box className="rule-domain-chart-box" sx={{ marginTop: "6px" }}>
          <h3>Configuration</h3>
          <Grid container columnSpacing={1.5} rowSpacing={2.5}>
            {isFromListPage && (
              <Grid item xl={3} lg={3} md={3}>
                <AutoCompleteRuleList
                  setIsLoading={setIsLoading}
                  handelOnChangeRule={handleRuleChange}
                  currentRuleId={selectedRule?.id}
                  className={`form-control-autocomplete ${
                    errors?.rule_id ? "has-error" : ""
                  }`}
                  required={true}
                  placeholder="Select Rule"
                  includeRuleId={true}
                  error={!!errors?.rule_id}
                  helperText={errors?.rule_id}
                />
              </Grid>
            )}
            {isAddModeActive && !isEditQuery && (
              <>
                <Grid item xl={3} lg={3} md={3}>
                  <Autocomplete
                    fullWidth
                    options={ruleQueriesType ?? []}
                    getOptionLabel={(option: any) =>
                      option ? `${option.replace("_", " ")} Queries` : ""
                    }
                    value={
                      ruleQueriesType?.find((option) => option === queryType) ||
                      null
                    }
                    renderInput={(params) => (
                      <TextField
                        label={
                          <span>
                            Select Query Type
                            <span className="required-asterisk">*</span>
                          </span>
                        }
                        name="queryType"
                        style={{ color: "#000000" }}
                        {...params}
                        placeholder="Select..."
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    )}
                    renderOption={(params: any, item: any) => (
                      <li
                        {...params}
                        key={item.key}
                        style={{
                          paddingTop: "2px",
                          paddingBottom: "2px",
                        }}
                      >
                        <ListItemText>
                          <span
                            style={{ textTransform: "capitalize" }}
                          >{`${item.replace("_", " ")} Queries`}</span>
                        </ListItemText>
                      </li>
                    )}
                    loadingText="Loading..."
                    onChange={(e, value) => {
                      handleSelectQueryType(value);
                      setShowAggregatedColumns(undefined);
                    }}
                    className={`form-control-autocomplete`}
                  />
                </Grid>
              </>
            )}
            {queryType === "Resource" && (
              <ResourceQueryTab
                researchQueryData={researchQueryData}
                setResearchQueryData={setResearchQueryData}
                handleTransition={handleTransition}
                checked={checked}
                resourcesData={allResourcesData}
                renderFrom="rule"
                ruleData={ruleData}
                columns={columns}
                setColumns={setColumns}
                isEditQuery={isEditQuery}
                setIsEditQuery={setIsEditQuery}
                editFormData={editFormData}
                setEditFormData={setEditFormData}
                setQueryType={setQueryType}
                handleSaveResearchQuery={(researchQuery) =>
                  handleSaveResearchQuery(researchQuery)
                }
                setEditorValue={setQueryBuilderTempValue}
                handleCancelClick={handleCancelClick}
                setIsAddModeActive={setIsAddModeActive}
                isFromListPage={isFromListPage}
                setErrors={setErrors}
                selectedRule={selectedRule}
                isFromExecution={isFromExecution}
              />
            )}
            {queryType === "External" && (
              <ExternalQueryTab
                researchQueryData={researchQueryData}
                setResearchQueryData={setResearchQueryData}
                handleTransition={handleTransition}
                checked={checked}
                columns={columns}
                setColumns={setColumns}
                isEditQuery={isEditQuery}
                setIsEditQuery={setIsEditQuery}
                editFormData={editFormData}
                setEditFormData={setEditFormData}
                setQueryType={setQueryType}
                handleSaveResearchQuery={(researchQuery) =>
                  handleSaveResearchQuery(researchQuery)
                }
                handleCancelClick={handleCancelClick}
                setIsAddModeActive={setIsAddModeActive}
                isFromListPage={isFromListPage}
                setErrors={setErrors}
                selectedRule={selectedRule}
                isFromExecution={isFromExecution}
              />
            )}
            {queryType === "Merged_Data" && (
              <MergeQueryTab
                researchQueryData={researchQueryData}
                setResearchQueryData={setResearchQueryData}
                handleTransition={handleTransition}
                checked={checked}
                columns={columns}
                setColumns={setColumns}
                isEditQuery={isEditQuery}
                setIsEditQuery={setIsEditQuery}
                editFormData={editFormData}
                setEditFormData={setEditFormData}
                setQueryType={setQueryType}
                queryType={queryType}
                handleSaveResearchQuery={(researchQuery) =>
                  handleSaveResearchQuery(researchQuery)
                }
                handleCancelClick={handleCancelClick}
                setIsAddModeActive={setIsAddModeActive}
                isFromListPage={isFromListPage}
                setErrors={setErrors}
                selectedRule={selectedRule}
                isFromExecution={isFromExecution}
              />
            )}
            {queryType === "Missing_Data" && (
              <MergeQueryTab
                researchQueryData={researchQueryData}
                setResearchQueryData={setResearchQueryData}
                handleTransition={handleTransition}
                checked={checked}
                columns={columns}
                setColumns={setColumns}
                isEditQuery={isEditQuery}
                setIsEditQuery={setIsEditQuery}
                editFormData={editFormData}
                setEditFormData={setEditFormData}
                setQueryType={setQueryType}
                queryType={queryType}
                handleSaveResearchQuery={(researchQuery) =>
                  handleSaveResearchQuery(researchQuery)
                }
                handleCancelClick={handleCancelClick}
                setIsAddModeActive={setIsAddModeActive}
                isFromListPage={isFromListPage}
                setErrors={setErrors}
                selectedRule={selectedRule}
                isFromExecution={isFromExecution}
              />
            )}
            {queryType === "Mismatched_Data" && (
              <MergeQueryTab
                researchQueryData={researchQueryData}
                setResearchQueryData={setResearchQueryData}
                handleTransition={handleTransition}
                checked={checked}
                columns={columns}
                setColumns={setColumns}
                isEditQuery={isEditQuery}
                setIsEditQuery={setIsEditQuery}
                editFormData={editFormData}
                setEditFormData={setEditFormData}
                setQueryType={setQueryType}
                queryType={queryType}
                handleSaveResearchQuery={(researchQuery) =>
                  handleSaveResearchQuery(researchQuery)
                }
                handleCancelClick={handleCancelClick}
                setIsAddModeActive={setIsAddModeActive}
                isFromListPage={isFromListPage}
                setErrors={setErrors}
                selectedRule={selectedRule}
                isFromExecution={isFromExecution}
              />
            )}
            {queryType === "Mixed" && !isEditQuery && (
              <Grid item>
                <label className="label-text line-height16">&nbsp;</label>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    columnGap: "8px",
                  }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    className="btn-orange"
                    onClick={handleConfigureMixedQuery}
                    disabled={isFromExecution}
                  >
                    + &nbsp;Configure
                  </Button>
                  <Tooltip
                    title="Click on configure to add/modify sub queries"
                    placement="top"
                    arrow
                  >
                    <span>
                      <InfoIcon sx={{ display: "block" }} />
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            )}
            {queryType === "Mixed" && isEditQuery && (
              <MixedQueryEditorDialog
                open={isEditQuery}
                onClose={handleCloseMixedDialog}
                onSave={handleSaveMixedQuery}
                queryName={mixedQueryName}
                setQueryName={handleMixedQueryNameChange}
                queryError={mixedErrors}
                subQueries={mixedSubQueries}
                activeIndex={mixedActiveIndex}
                setActiveIndex={setMixedActiveIndex}
                columns={columns}
                setColumns={setColumns}
                resourcesData={allResourcesData}
                linkedServicesData={linkedServicesData}
                mainQuery={mixedMainQuery}
                setMainQuery={handleMixedMainQueryChange}
                handlers={{
                  handleTypeChange: mixedHandleTypeChange,
                  handleFieldChange: mixedHandleFieldChange,
                  handleLinkedServiceChange: mixedHandleLinkedServiceChange,
                  handleQueryChange: mixedHandleQueryChange,
                  handleEditorFocus: mixedHandleEditorFocus,
                  handleEditorBlur: mixedHandleEditorBlur,
                  removeSubQuery: mixedRemoveSubQuery,
                  handleToggleActive: mixedHandleToggleActive,
                  getDependencyDisplayLabel: mixedGetDependencyDisplayLabel,
                  handleSetExternalColumns: mixedHandleSetExternalColumns,
                }}
                getSubQueryLabel={getSubQueryLabel}
                onAddSource={
                  mixedSubQueries.length < MAX_SUBQUERIES
                    ? mixedAddSubQuery
                    : undefined
                }
                errors={mixedErrors}
                isReadOnly={resolveMixedQueryReadOnly({
                  isViewOnly,
                  isFromExecution,
                  allowExecutionOverride,
                })}
                availableSubTypes={ruleSubQueriesType}
              />
            )}
            {!isFromListPage && queryType === "" && (
              <Grid
                item
                xs
                sx={{
                  display: "flex",
                  columnGap: "8px",
                  textAlign: "right",
                  justifyContent: "flex-end",
                  alignSelf: "flex-end",
                }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  className="btn-orange btn-dark"
                  disabled={isFromListPage && selectedRule.id === null}
                  onClick={handleCancelClick}
                >
                  Cancel
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* Show query table if not in add-new mode, or if in add-new mode with data */}
      {(() => {
        const shouldShowQueryTable =
          !isFromListPage || researchQueryData?.length > 0;
        const showQueryActions =
          shouldShowQueryTable &&
          researchQueryData?.length > 0 &&
          !isFromExecution;
        const showViewJson = !isViewOnly && !isFromExecution;
        return (
          <Box>
            {/* Clone button - positioned near the + Query button */}
            {(showQueryActions || showViewJson) && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: 2,
                  mb: 2,
                  columnGap: 1.5,
                }}
              >
                {showQueryActions && (
                  <>
                    {isViewOnly &&
                      currentRuleId &&
                      canEditRuleResearchQuery && (
                        <Tooltip
                          title="Edit Comparison Research Query"
                          placement="top"
                          arrow
                        >
                          <button
                            className="btn-nostyle icon-btn-edit"
                            onClick={() =>
                              navigate(
                                `/comparison-research-queries/edit/${currentRuleId}`,
                              )
                            }
                          >
                            <IconBtnEditBase ariaLabel="Edit Comparison Research Query" />
                          </button>
                        </Tooltip>
                      )}
                    <Tooltip title="Clone Research Query" placement="top" arrow>
                      <Button
                        variant="contained"
                        color="secondary"
                        className="btn-orange"
                        onClick={handleCloneComparisonResearchQuery}
                      >
                        Clone
                      </Button>
                    </Tooltip>
                  </>
                )}
                {showViewJson && (
                  <ViewJsonSplitButton
                    onViewJson={() => setIsPayloadDialogOpen(true)}
                    onCopyJson={handleCopyPayload}
                  />
                )}
              </Box>
            )}
            {shouldShowQueryTable && (
              <DataTable
                dataRows={researchQueryData}
                dataColumns={researchColumns}
                dataListTitle="Query List"
                className="dataTable hide-progress-icon no-buttons1"
                paginationMode="client"
                handleTransition={handleTransition}
                showAddTxnButton={
                  isFromExecution
                    ? true
                    : isViewOnly ||
                      isAddModeActive ||
                      isEditQuery ||
                      !canAddRuleResearchQuery
                }
                singlePageMaxHeightDiff={327}
                hideExportButton={true}
              />
            )}
          </Box>
        );
      })()}
      <CloneComparisonResearchQueryDialog
        cloneComparisonResearchQueryDialog={cloneComparisonResearchQueryDialog}
        handleCloseCloneComparisonResearchQuery={
          handleCloseCloneComparisonResearchQuery
        }
        cloneComparisonResearchQueryData={cloneComparisonResearchQueryData}
        setCloneComparisonResearchQueryData={
          setCloneComparisonResearchQueryData
        }
        onSaveCloneComparisonResearchQuery={onSaveCloneComparisonResearchQuery}
        setIsLoading={setLoading("cloneResearchQuery")}
      />
      <CommentBeforeUpdateDialog
        title={"Confirm updating Research Query"}
        dialogContent={
          <div>
            <p className="m-0 mb-2">
              Add a note before updating the Research Query
            </p>
            <textarea
              value={comment}
              className={`form-control-1 max-60`}
              onChange={(e) => handleCommentChange(e)}
            />
          </div>
        }
        openConfirmation={openCommentConfirmation}
        handleSaveComment={handleSaveComment}
        handleCancel={handleCancelComment}
      />
      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        dialogTitle="Comparison Research Query JSON"
        payload={buildPayload()}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </>
  );
}
